import React from 'react';
import DefaultLayout from '../../../layouts/DefaultLayout';
import TemplateColumn, { TemplateColumnWithSidebarContainer } from '../../../layouts/TemplateColumn';
import { PageHeader, PageSection, DataTable, RelatedContent, CrossLink, IndicationPageIntro, AnchorLinks, TextBlock, SecondaryCallout, ReferencesBlock} from '../../../components';
import { StaticImage } from 'gatsby-plugin-image';
import nsclcSafetyTable from "../../../assets/NSCLC-1L-Combo-safety-small-V2.svg"
import GlobalSafetyData, { globalSafetyBlock } from './_global_safety_data'
import { jobCodes } from '../../../util/data/jobCodes';

// SEO INFORMATION
const pageMeta = {
    title: `KEYNOTE-189: Selected Adverse Reactions`,
    keywords: `keynote-189 adverse reactions`,
    description: `Health care professionals may review selected immune mediated adverse reactions for KEYTRUDA® (pembrolizumab) and selected adverse reactions reported in the KEYNOTE-189 clinical trial in patients with metastatic nonsquamous non–small cell lung cancer (NSCLC).`,
    schemaJsonLD: [
        `{"@context":"https://schema.org/","url":"https://www.keytrudahcp.com/safety/adverse-reactions/nsclc-nonsquamous-first-line-combination-therapy/","@type":"MedicalAudience","audienceType":"US Health Care Professionals","geographicArea":"This site is intended for health care professionals of the United States, its territories, and Puerto Rico."}`,
        `{"@context":"https://schema.org/","url":"https://www.keytrudahcp.com/safety/adverse-reactions/nsclc-nonsquamous-first-line-combination-therapy/","@type":"MedicalWebPage","name":"Selected Adverse Reactions in KEYNOTE⁠-⁠189 for KEYTRUDA","description":"Adverse reactions leading to the interruption of KEYTRUDA occurred in 53% of patients; the most common adverse reactions or laboratory abnormalities leading to interruption of KEYTRUDA (≥2%) were neutropenia (13%), asthenia/fatigue (7%), anemia (7%), thrombocytopenia (5%), diarrhea (4%), pneumonia (4%), increased blood creatinine (3%), dyspnea (2%), febrile neutropenia (2%), upper respiratory tract infection (2%), increased ALT (2%), and pyrexia (2%)."}`
    ]
}

// image alts - so they do not repeat in mobile & desktop
const lungNSQMonoSafetyAlt = "Adverse Reactions Occurring in ≥20% of Patients Receiving KEYTRUDA® (pembrolizumab) With Plat/Pem in KEYNOTE-189"

//from /src/util/data/indications.js
const indicationId = 4;
const jobCode = jobCodes[4].jobCode;

const relatedContent = [
    {
        section: 'Resources',
        link: {
            url: '/resources/mechanism-of-action/',
            label: 'Mechanism of Action'
        }
    },
    {
        section: 'Resources',
        link: {
            url: 'https://www.merckaccessprogram-keytruda.com/',
            label: 'Access & Reimbursement Support'
        }
    }
];

const crossLinkContent = {
    sections : [
        {
            heading: "Additional Safety Data",
            items: [
                { text: '1L Combo Therapy – Squamous', url: '/safety/adverse-reactions/nsclc-squamous-first-line-combination-therapy/' },
                { text: '1L Monotherapy – Nonsquamous & Squamous', url: '/safety/adverse-reactions/nsclc-first-line-monotherapy/' },
                { text: '2L Monotherapy – Nonsquamous & Squamous', url: '/safety/adverse-reactions/nsclc-second-line-monotherapy/' },
                { text: 'Neoadjuvant Combo Therapy Followed by Adjuvant Monotherapy After Surgery for Resectable (Tumors ≥4 cm or Node Positive) NSCLC', url: '/safety/adverse-reactions/nsclc-perioperative-regimen/' },
                { text: 'Adjuvant Therapy for Stage IB (T2a ≥4 cm), Stage II, or Stage IIIA NSCLC', url: '/safety/adverse-reactions/nsclc-adjuvant-therapy/' },
                ]
        },
        {
            heading: "Related Information",
            items: [
                { text: 'Treatment Monitoring & Management', url: '/safety/monitoring-managing-adverse-reactions/' },
                { text: 'Dose Modifications', url: '/dosing/dose-modifications/' },
                ]
        },
    ],
    interestLinks : {
        heading: "You Might Be Interested In",
        items: [
            { eyebrow: 'Resources', text: 'Mechanism of Action', url: '/resources/mechanism-of-action/' },
        ],
    }
};

const anchorLinkModelData = [
    {label: 'Incidence of Selected Immune-Mediated Adverse Reactions'},
    {label: 'Management of Selected Immune-Mediated Adverse Reactions'},
    {label: 'Selected Adverse Reactions in KEYNOTE&#8288;-&#8288;189 for KEYTRUDA'}
]

const nsclc1LcomboNsqDataTableFootnotes = [
    {
        label: 'b.',
        text: 'Graded per NCI-CTCAE v4.03.'
    },
    {
        label: 'c.',
        text: 'Includes asthenia and fatigue.'
    },
    {
        label: 'd.',
        text: 'Includes genital rash, rash, rash generalized, rash macular, rash maculopapular, rash papular, rash pruritic, and rash pustular.'
    },

]
const pageReferences = [
    {
        label: "1.",
        text: "Gandhi L, Rodríguez-Abreu D, Gadgeel S, et al; for the KEYNOTE⁠-⁠189 investigators. Pembrolizumab plus chemotherapy in metastatic non–small-cell lung cancer. <i>N Engl J Med.</i> 2018;378(22):2078–2092. doi:10.1056/NEJMoa1801005"
    },
    
]
const nsclc1LcomboNsqDataTableDefinitions = "plat/pem = (cisplatin or carboplatin) and pemetrexed; NCI-CTCAE = National Cancer Institute-Common Terminology Criteria for Adverse Events."

const Page = ({ location }) => {

    return (
        <DefaultLayout indicationId={indicationId} jobCode={jobCode} pageMeta={ pageMeta } location={ location } >
            <div data-template-name="template-e">
                {/* Begin Intro Column */}
                <TemplateColumn id="template-e-intro" maxWidth={824}>
                    <PageHeader title="Selected Adverse Reactions" isBranding />
                   <PageSection bgColor="white">
                        <IndicationPageIntro indicationId={indicationId}/>
                   </PageSection>
                </TemplateColumn>
                {/* End Intro Column */}

                <TemplateColumnWithSidebarContainer>
                {/* Begin Main Column */}
                    <TemplateColumn id="template-e-main">
                        <PageSection bgColor="cloud" title="Severe and Fatal Immune-Mediated Adverse Reactions">
                            <TextBlock>{globalSafetyBlock}</TextBlock>
                            <AnchorLinks items={anchorLinkModelData} />
                        </PageSection>

                        <GlobalSafetyData />

                        <PageSection title={anchorLinkModelData[2].label}>
                            <TextBlock>
                                <p><b>KEYTRUDA was permanently discontinued due to adverse reactions in 20% of 405 patients.</b> The most common adverse reactions resulting in discontinuation of KEYTRUDA were pneumonitis (3%) and acute kidney injury (2%).</p>
                            </TextBlock>
                            <TextBlock>
                                <p><b>Adverse reactions leading to the interruption of KEYTRUDA occurred in 53% of patients;</b> the most common adverse reactions or laboratory abnormalities leading to interruption of KEYTRUDA (≥2%) were neutropenia (13%), asthenia/fatigue (7%), anemia (7%), thrombocytopenia (5%), diarrhea (4%), pneumonia (4%), increased blood creatinine (3%), dyspnea (2%), febrile neutropenia (2%), upper respiratory tract infection (2%), increased ALT (2%), and pyrexia (2%).</p>
                            </TextBlock>
                            <SecondaryCallout 
                                title="At the initial analysis of KEYNOTE-189 (median follow-up time of 10.5 months), 80% of patients treated with KEYTRUDA did not discontinue KEYTRUDA due to adverse reactions<sup>1</sup>" 
                                alt="" 
                                text="Additional patients discontinued treatment for reasons other than adverse reactions"
                            />

                            <DataTable
                                title='Adverse Reactions Occurring in ≥20% of Patients'
                                footnotes={nsclc1LcomboNsqDataTableFootnotes}
                                definitions={nsclc1LcomboNsqDataTableDefinitions}
                                mobileImage={() => <img src={nsclcSafetyTable} style={{width:'100%', maxWidth:'500px', margin: '0 auto'}} alt={lungNSQMonoSafetyAlt} />}
                                desktopImage={() => <StaticImage quality={90} placeholder="DOMINANT_COLOR" src="../../../assets/NSCLC-1L-Combo-safety-large-V2.png" alt={lungNSQMonoSafetyAlt} />}
                            />
                        </PageSection>
                    </TemplateColumn>
                {/* End Main Column */}

                    <TemplateColumn id="template-e-sidebar">
                        <PageSection bgColor="mist">
                            <CrossLink {...crossLinkContent} />
                        </PageSection>
                    </TemplateColumn>

                </TemplateColumnWithSidebarContainer>

                <TemplateColumn id="template-e-secondary">
                    <PageSection bgColor="keytrudaGreen">
                        <RelatedContent items={relatedContent}/>
                    </PageSection>
                    <PageSection bgColor="mist">
                        <ReferencesBlock references={pageReferences} />
                    </PageSection>
                </TemplateColumn>
            </div>
        </DefaultLayout>
    );
};

export default Page;